import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  CreateRowConfigDto,
  DataIngestionEntityDto,
} from '@asset-metrix/portal-gateway-client-js';
import {
  Button,
  Checkbox,
  ConditionalFilterPlugin,
  GoogleFontIcon,
  jsonEditorWidgets,
  MultiSelect,
  Tooltip,
  Widget as PortalWidget,
} from '@asset-metrix/portal-ui';
import {
  JsonError,
  useEditor,
  useEditorPlugin,
  Widget,
} from '@sagold/react-json-editor';
import { useQuery } from '@tanstack/react-query';
import clsx from 'clsx';
import deepEqual from 'deep-equal';

import { downloadDataIngestionTemplate } from '../../api/portalGateway/downloadDataIngestionTemplate';
import { getDataIngestionEntities } from '../../api/portalGateway/getDataIngestionEntities';
import {
  searchTableRowModalValues,
  SearchTableRowModalValuesParamsData,
} from '../../api/portalGateway/searchTableRowModalValues';
import { useEffectiveGlobalSelections } from '../../store/hooks/useEffectiveGlobalSelections';
import { useSelections } from '../../store/hooks/useSelections';
import { actionWithAsyncNotification } from '../../utils/actionWithAsyncNotification';
import { extractErrorMessage } from '../../utils/errorMessage';

import { portalWidgetContentStyle } from './dataIngestionPortalWidgetStyles';

import styles from './dataIngestionModal.module.scss';

export function ConfigureAndDownloadStep({
  config,
  contentId,
  instanceId,
  onValidationErrorsChange,
  onImportDataChange,
}: {
  config: CreateRowConfigDto;
  contentId: string;
  instanceId: string;
  onValidationErrorsChange?: (errors: JsonError[]) => void;
  onImportDataChange?: (importData: Record<string, unknown>) => void;
}) {
  const [selectedEntities, setSelectedEntities] = useState<
    DataIngestionEntityDto['value'][]
  >([]);
  const [prefillWithAcceptedData, setPrefillWithAcceptedData] = useState(false);
  const previousDataRef = useRef<unknown>({});
  const globalFilterSelections = useEffectiveGlobalSelections();
  const instanceFilterSelections = useSelections(instanceId);
  const pageFilterSelections = useMemo(
    () => ({ ...globalFilterSelections, ...instanceFilterSelections }),
    [globalFilterSelections, instanceFilterSelections]
  );
  const [validationErrors, setValidationErrors] = useState<JsonError[]>([]);

  const { t } = useTranslation();

  const mapEditorData = useCallback((data?: Record<string, unknown>) => {
    if (!data) {
      return {};
    }

    return Object.fromEntries(
      Object.entries(data).map(([key, value]) => {
        return [
          key,
          Array.isArray(value) && value.length === 1 ? value[0] : value,
        ];
      })
    );
  }, []);

  const {
    data,
    isPending: entitiesLoading,
    error: entitiesQueryError,
  } = useQuery({
    queryKey: ['dataIngestionEntities', contentId],
    queryFn: () => getDataIngestionEntities(contentId),
  });

  const entityOptions = useMemo<DataIngestionEntityDto[]>(
    () => data?.data ?? [],
    [data]
  );

  const entitiesError = entitiesQueryError
    ? extractErrorMessage(entitiesQueryError)
    : undefined;

  const editor = useEditor({
    widgets: jsonEditorWidgets,
    schema: config.jsonSchema ?? {},
    onChange: (_data, _root, editor) => {
      setValidationErrors(editor.getErrors());

      if (!deepEqual(_data, previousDataRef.current)) {
        previousDataRef.current = _data;
        onImportDataChange?.(mapEditorData(_data as Record<string, unknown>));
      }
    },
    validate: false,
    addOptionalProps: true,
  });

  useEditorPlugin(editor, ConditionalFilterPlugin, {
    fetchOptions: async (
      formData: NonNullable<SearchTableRowModalValuesParamsData>['data']
    ) => {
      return await searchTableRowModalValues(contentId, {
        data: formData,
        context: { pageFilterSelections },
      });
    },
  });

  useEffect(() => {
    editor?.validate();
    if (editor) {
      setValidationErrors(editor.getErrors());
    }
  }, [editor]);

  useEffect(() => {
    onValidationErrorsChange?.(validationErrors);
  }, [validationErrors, onValidationErrorsChange]);

  const downloadTemplate = useCallback(() => {
    const editorData = editor?.getData();
    const mappedEditorData = mapEditorData(editorData);

    void actionWithAsyncNotification(
      () =>
        downloadDataIngestionTemplate(contentId, {
          entityData: selectedEntities.map((entity) => ({
            label:
              entityOptions.find((option) => option.value === entity)?.label ??
              '',
            value: entity.toString(),
          })),
          importData: mappedEditorData,
          prefillWithAcceptedData,
        }),
      {
        startMessage: t(
          'app.dataIngestion.configureAndDownload.templateDownload.loading'
        ),
        successMessage: t(
          'app.dataIngestion.configureAndDownload.templateDownload.success'
        ),
        errorMessage: (error) =>
          t('app.dataIngestion.configureAndDownload.templateDownload.error', {
            message: extractErrorMessage(error),
          }),
      }
    );
  }, [
    editor,
    contentId,
    entityOptions,
    selectedEntities,
    prefillWithAcceptedData,
    t,
    mapEditorData,
  ]);

  return (
    <div
      className={clsx(
        styles['stepContentColumns'],
        'portal-form rje-form rje-theme rje-theme--light'
      )}
    >
      <PortalWidget
        contentStyle={portalWidgetContentStyle}
        classNames={{
          root: styles['modalWidget'],
          title: styles['modalWidgetTitle'],
        }}
        title={t('app.dataIngestion.configureAndDownload.configureImport')}
      >
        {config.jsonSchema ? (
          <Widget editor={editor} />
        ) : (
          <p>{t('app.dataIngestion.configureAndDownload.noImportConfig')}</p>
        )}
      </PortalWidget>
      <PortalWidget
        contentStyle={portalWidgetContentStyle}
        classNames={{
          root: styles['modalWidget'],
          title: styles['modalWidgetTitle'],
        }}
        title={t('app.dataIngestion.configureAndDownload.selectScope')}
      >
        <div className={styles['configureDownloadWidgetBody']}>
          <MultiSelect
            data={entityOptions}
            label={t('app.dataIngestion.configureAndDownload.entitiesLabel')}
            loading={entitiesLoading}
            error={entitiesError}
            value={selectedEntities}
            onChange={setSelectedEntities}
          />
          <div className={styles['prefillDataCheckbox']}>
            <Checkbox
              label={t(
                'app.dataIngestion.configureAndDownload.prefillDataLabel'
              )}
              checked={prefillWithAcceptedData}
              onChange={setPrefillWithAcceptedData}
            />
            <Tooltip
              label={t(
                'app.dataIngestion.configureAndDownload.prefillDataHelpText'
              )}
              usePortal={true}
            >
              <GoogleFontIcon name="info" />
            </Tooltip>
          </div>
          <Button
            variant="primary"
            onClick={downloadTemplate}
            disabled={
              selectedEntities.length === 0 || validationErrors.length > 0
            }
          >
            {t('app.dataIngestion.configureAndDownload.downloadTemplate')}
          </Button>
        </div>
      </PortalWidget>
    </div>
  );
}
